import { observer } from 'mobx-react-lite'
import type { SelectionState } from '@/infra/selection-state'
import { type PayoutReviewViewModel } from '@/features/payouts/admin/review/PayoutReviewViewModel'
import { type PendingTransactionViewModel } from '@/features/payouts/admin/review/PendingTransactionViewModel'
import {
  PendingTransactionRow,
  PendingTransactionsTable,
  PendingTransactionsTableHeader,
} from '@/features/payouts/admin/review/components/PendingTransactionsTable'
import { match } from '@/utils/pattern-match-util'
import { useToggle } from '@/hooks/state-hooks'
import { FilterControlsContainer } from '@/components/FilterControls/FilterControlsContainer'
import { VirtualTableContainer } from '@/components/VirtualTableContainer'

export interface PendingTransactionsTableContainerProps {
  vm: PayoutReviewViewModel
}

export const PendingTransactionsTableContainer = observer(
  function PendingTransactionsTableContainer({
    vm,
  }: PendingTransactionsTableContainerProps) {
    const filtersShowing = useToggle(false)

    return (
      <PendingTransactionsTable
        actionsDisabled={vm.actionsDisabled}
        searchText={vm.searchText.value}
        onSearchTextChange={vm.searchText.change}
        filtersShowing={filtersShowing.isEnabled}
        onToggleFilters={filtersShowing.toggle}
        appliedFilterCount={vm.filters.appliedFilterCount}
        isApprovePending={vm.isApprovePending}
        selectedCount={vm.selected.size}
        onApproveSelected={vm.approveSelectedTransactions}
        approveMatchingFilterDisabled={vm.approveMatchingFilterDisabled}
        onApproveMatchingFilter={vm.approveTransactionsMatchingFilter}
        isExportPending={vm.exportCsv.pending}
        onExportCsv={vm.exportCsv}
        filterControls={
          <FilterControlsContainer
            vm={vm.filters}
            active={filtersShowing.isEnabled}
            editorTestId="job"
            testId="job-filter"
          />
        }
        showTable={vm.showTable}
        appliedFilters={vm.filtersApplied}
        onClearFilters={vm.clearFilters}
      >
        <VirtualTableContainer
          totalCount={vm.pendingTransactions.length}
          overscan={400}
          scroller={vm.pendingTransactionsScroller}
          itemContent={(index) => {
            const pendingTransactionVm = vm.pendingTransactions[index]
            return (
              <PendingTransactionRowContainer
                vm={vm}
                key={pendingTransactionVm.transaction.id}
                pendingTransactionVm={pendingTransactionVm}
              />
            )
          }}
          headerContent={() => (
            <PendingTransactionsTableHeaderContainer vm={vm} />
          )}
        />
      </PendingTransactionsTable>
    )
  },
)

interface PendingTransactionsTableHeaderContainerProps {
  vm: PayoutReviewViewModel
}

const PendingTransactionsTableHeaderContainer = observer(
  function PendingTransactionsTableHeaderContainer({
    vm,
  }: PendingTransactionsTableHeaderContainerProps) {
    return (
      <PendingTransactionsTableHeader
        actionsDisabled={vm.actionsDisabled}
        onToggleSelection={vm.toggleListSelection}
        checked={match<SelectionState, boolean | 'indeterminate'>(
          vm.selectionState,
          {
            AllSelected: () => true,
            NoneSelected: () => false,
            SomeSelected: () => 'indeterminate',
          },
        )}
      />
    )
  },
)

interface PendingTransactionRowContainerProps {
  vm: PayoutReviewViewModel
  pendingTransactionVm: PendingTransactionViewModel
}

const PendingTransactionRowContainer = observer(
  function PendingTransactionRowContainer({
    vm,
    pendingTransactionVm,
  }: PendingTransactionRowContainerProps) {
    const transaction = pendingTransactionVm.transaction
    return (
      <PendingTransactionRow
        actionsDisabled={vm.actionsDisabled}
        selected={pendingTransactionVm.selected}
        onToggleSelection={pendingTransactionVm.toggleSelection}
        isPending={pendingTransactionVm.approve.pending}
        id={transaction.id}
        source={transaction.source}
        memo={transaction.memo}
        reason={transaction.reason}
        jobId={transaction.jobId ?? null}
        lastFailureDescription={transaction.lastFailureDescription}
        recipientName={transaction.recipient.displayName}
        recipientAvatar={transaction.recipient.avatar}
        recipientAvatarFallback={transaction.recipient.avatarFallback}
        amount={transaction.amount}
        workspaceName={transaction.workspace.name}
        workspaceIcon={transaction.workspace.branding.logos.icon}
        shortId={transaction.shortId}
        createdAt={transaction.createdAt}
        transactedAt={transaction.transactedAt}
        iteration={transaction.iteration}
        flags={transaction.flags.slice()}
        onApprove={pendingTransactionVm.approve}
        onRequestCancellation={pendingTransactionVm.requestCancellation}
        onViewJobInHQ={pendingTransactionVm.viewJobInHQ}
        onViewJob={pendingTransactionVm.viewJob}
      />
    )
  },
)
